import { ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "@/utils/cn";
import { MoveRight } from "lucide-react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: "primary" | "secondary" | "outline" | "ghost";
    size?: "sm" | "md" | "lg" | "xl";
    icon?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "primary", size = "md", icon, children, ...props }, ref) => {
        return (
            <button
                ref={ref}
                className={cn(
                    "relative inline-flex items-center justify-center rounded-xl font-bold transition-all duration-300 disabled:pointer-events-none disabled:opacity-50 active:scale-95",
                    {
                        // Primary: Strong contrast with clear press feedback
                        "bg-gradient-to-r from-brand-700 to-brand-600 text-white shadow-[0_4px_0_rgb(4,120,87)] hover:shadow-[0_6px_0_rgb(4,120,87)] hover:-translate-y-0.5 active:translate-y-1 active:shadow-none": variant === "primary",

                        // Secondary: Dark
                        "bg-surface-800 text-white border border-surface-700 hover:bg-surface-700 hover:border-surface-600": variant === "secondary",

                        // Outline
                        "border-2 border-surface-700 bg-transparent text-surface-300 hover:border-brand-500 hover:text-brand-300": variant === "outline",

                        // Ghost
                        "hover:bg-white/5 text-surface-400 hover:text-white": variant === "ghost",

                        "h-10 px-5 text-sm": size === "sm",
                        "h-12 px-7 text-base": size === "md",
                        "h-14 px-9 text-lg": size === "lg",
                        "h-16 px-10 text-xl": size === "xl",
                    },
                    className
                )}
                {...props}
            >
                {children}
                {icon && <MoveRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </button>
        );
    }
);

Button.displayName = "Button";

export { Button };
